import { useMemo } from 'react';
import { ShieldCheck } from 'lucide-react';

interface Props {
  gates: string[];
  byStory: Record<string, Record<string, 'pass' | 'fail' | 'skipped'>>;
}

function formatGateName(gate: string): string {
  return gate
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export function QualityGateMatrix({ gates, byStory }: Props) {
  const storyIds = useMemo(() => Object.keys(byStory).sort(), [byStory]);

  const gateStats = useMemo(() => {
    const stats: Record<string, { passed: number; total: number }> = {};
    for (const gate of gates) {
      let passed = 0;
      let total = 0;
      for (const storyId of storyIds) {
        const result = byStory[storyId]?.[gate];
        if (result && result !== 'skipped') {
          total++;
          if (result === 'pass') passed++;
        }
      }
      stats[gate] = { passed, total };
    }
    return stats;
  }, [gates, byStory, storyIds]);

  if (gates.length === 0 || storyIds.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-6 text-center">
        <ShieldCheck size={20} className="mx-auto text-neutral-300 mb-2" />
        <p className="text-[11px] text-neutral-400">No quality gate data yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-100">
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Quality Gates</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="border-b border-neutral-100 bg-neutral-50/50">
              <th className="text-left px-3 py-2 font-semibold text-neutral-500 sticky left-0 bg-neutral-50/50">
                Story
              </th>
              {gates.map(gate => (
                <th key={gate} className="text-center px-2 py-2 font-semibold text-neutral-500 whitespace-nowrap">
                  {formatGateName(gate)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-50">
            {storyIds.map(storyId => (
              <tr key={storyId} className="hover:bg-neutral-50/50 transition-colors">
                <td className="px-3 py-1.5 font-mono font-medium text-neutral-600 sticky left-0 bg-white">
                  {storyId}
                </td>
                {gates.map(gate => {
                  const result = byStory[storyId]?.[gate];
                  return (
                    <td key={gate} className="text-center px-2 py-1.5">
                      {result === 'pass' ? (
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" title="Passed" />
                      ) : result === 'fail' ? (
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" title="Failed" />
                      ) : (
                        <span className="inline-block w-2.5 h-0.5 rounded bg-neutral-300" title="Skipped" />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-neutral-200 bg-neutral-50/50">
              <td className="px-3 py-2 font-semibold text-neutral-500 sticky left-0 bg-neutral-50/50">
                Summary
              </td>
              {gates.map(gate => {
                const { passed, total } = gateStats[gate];
                const allPassed = total > 0 && passed === total;
                return (
                  <td key={gate} className="text-center px-2 py-2">
                    <span className={`font-semibold ${
                      allPassed ? 'text-emerald-600' :
                      total > 0 ? 'text-amber-600' :
                      'text-neutral-400'
                    }`}>
                      {passed}/{total}
                    </span>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
